import React, { useState, useMemo } from 'react';
import { DatePicker, Modal } from 'antd';
import { CalendarOutlined, CloseCircleOutlined } from '@ant-design/icons';
import dayjs, { Dayjs } from 'dayjs';
import Picker from 'react-mobile-picker';
import { useResponsive } from '../../hooks/useResponsive';
import './MobileDateRangePickerV2.css';

const { RangePicker } = DatePicker;

interface MobileDateRangePickerProps {
  value?: [Dayjs | null, Dayjs | null] | null;
  onChange?: (dates: [Dayjs | null, Dayjs | null] | null) => void;
  placeholder?: [string, string];
  format?: string;
  style?: React.CSSProperties;
  size?: 'small' | 'middle' | 'large';
  allowClear?: boolean;
}

interface PickerValue {
  startYear: string;
  startMonth: string;
  startDay: string;
  endYear: string;
  endMonth: string;
  endDay: string;
}

/**
 * iOS-compatible date range picker using react-mobile-picker for mobile
 * and Ant Design RangePicker for desktop.
 */
const MobileDateRangePickerV2: React.FC<MobileDateRangePickerProps> = ({
  value,
  onChange,
  placeholder = ['Inicio', 'Fin'],
  format = 'DD/MM/YYYY',
  style,
  size = 'middle',
  allowClear = true,
}) => {
  const { isMobile } = useResponsive();
  const [isModalOpen, setIsModalOpen] = useState(false);

  // Generate year, month, day options
  const years = useMemo(() => {
    const currentYear = dayjs().year();
    const yearArray: string[] = [];
    for (let i = currentYear - 10; i <= currentYear + 5; i++) {
      yearArray.push(i.toString());
    }
    return yearArray;
  }, []);

  const months = useMemo(() => {
    return Array.from({ length: 12 }, (_, i) => (i + 1).toString().padStart(2, '0'));
  }, []);

  const getDaysInMonth = (year: string, month: string): string[] => {
    const daysCount = dayjs(`${year}-${month}-01`).daysInMonth();
    return Array.from({ length: daysCount }, (_, i) => (i + 1).toString().padStart(2, '0'));
  };

  // Initialize picker values from props
  const getInitialPickerValue = (): PickerValue => {
    const now = dayjs();
    const startDate = value?.[0] || now;
    const endDate = value?.[1] || now;

    return {
      startYear: startDate.year().toString(),
      startMonth: (startDate.month() + 1).toString().padStart(2, '0'),
      startDay: startDate.date().toString().padStart(2, '0'),
      endYear: endDate.year().toString(),
      endMonth: (endDate.month() + 1).toString().padStart(2, '0'),
      endDay: endDate.date().toString().padStart(2, '0'),
    };
  };

  const [pickerValue, setPickerValue] = useState<PickerValue>(getInitialPickerValue());

  // Generate days based on selected month/year
  const startDays = useMemo(
    () => getDaysInMonth(pickerValue.startYear, pickerValue.startMonth),
    [pickerValue.startYear, pickerValue.startMonth]
  );

  const endDays = useMemo(
    () => getDaysInMonth(pickerValue.endYear, pickerValue.endMonth),
    [pickerValue.endYear, pickerValue.endMonth]
  );

  const handleOpenModal = () => {
    // Reset picker value to current value when opening
    setPickerValue(getInitialPickerValue());
    setIsModalOpen(true);
  };

  const handleConfirm = () => {
    const startDate = dayjs(
      `${pickerValue.startYear}-${pickerValue.startMonth}-${pickerValue.startDay}`
    );
    const endDate = dayjs(
      `${pickerValue.endYear}-${pickerValue.endMonth}-${pickerValue.endDay}`
    );

    // Validate: start date should not be after end date
    if (startDate.isAfter(endDate)) {
      // Swap dates if start is after end
      onChange?.([endDate, startDate]);
    } else {
      onChange?.([startDate, endDate]);
    }

    setIsModalOpen(false);
  };

  const handleCancel = () => {
    setIsModalOpen(false);
  };

  const handleClear = () => {
    onChange?.(null);
  };

  const hasValue = value && value[0] && value[1];

  const displayValue = hasValue
    ? `${value[0]!.format(format)} - ${value[1]!.format(format)}`
    : '';

  // Mobile: Use react-mobile-picker in Modal
  if (isMobile) {
    return (
      <>
        <div
          className="mobile-date-range-picker-trigger"
          style={style}
          onClick={handleOpenModal}
        >
          <CalendarOutlined className="picker-icon" />
          <span className={`picker-text ${!hasValue ? 'placeholder' : ''}`}>
            {displayValue || `${placeholder[0]} - ${placeholder[1]}`}
          </span>
          {allowClear && hasValue && (
            <CloseCircleOutlined
              className="picker-clear"
              onClick={(e) => {
                e.stopPropagation();
                handleClear();
              }}
            />
          )}
        </div>

        <Modal
          title="Seleccionar Rango de Fechas"
          open={isModalOpen}
          onOk={handleConfirm}
          onCancel={handleCancel}
          okText="Confirmar"
          cancelText="Cancelar"
          width="90%"
          centered
          className="mobile-date-range-modal"
        >
          <div className="mobile-date-range-content">
            {/* Start Date Section */}
            <div className="date-section">
              <div className="section-label">Fecha Inicio</div>
              <Picker
                value={{
                  year: pickerValue.startYear,
                  month: pickerValue.startMonth,
                  day: pickerValue.startDay,
                }}
                onChange={(newValue) => {
                  setPickerValue((prev) => ({
                    ...prev,
                    startYear: newValue.year as string,
                    startMonth: newValue.month as string,
                    startDay: newValue.day as string,
                  }));
                }}
                wheelMode="normal"
                height={200}
              >
                <Picker.Column name="year">
                  {years.map((year) => (
                    <Picker.Item key={year} value={year}>
                      {year}
                    </Picker.Item>
                  ))}
                </Picker.Column>
                <Picker.Column name="month">
                  {months.map((month) => (
                    <Picker.Item key={month} value={month}>
                      {month}
                    </Picker.Item>
                  ))}
                </Picker.Column>
                <Picker.Column name="day">
                  {startDays.map((day) => (
                    <Picker.Item key={day} value={day}>
                      {day}
                    </Picker.Item>
                  ))}
                </Picker.Column>
              </Picker>
            </div>

            {/* End Date Section */}
            <div className="date-section">
              <div className="section-label">Fecha Fin</div>
              <Picker
                value={{
                  year: pickerValue.endYear,
                  month: pickerValue.endMonth,
                  day: pickerValue.endDay,
                }}
                onChange={(newValue) => {
                  setPickerValue((prev) => ({
                    ...prev,
                    endYear: newValue.year as string,
                    endMonth: newValue.month as string,
                    endDay: newValue.day as string,
                  }));
                }}
                wheelMode="normal"
                height={200}
              >
                <Picker.Column name="year">
                  {years.map((year) => (
                    <Picker.Item key={year} value={year}>
                      {year}
                    </Picker.Item>
                  ))}
                </Picker.Column>
                <Picker.Column name="month">
                  {months.map((month) => (
                    <Picker.Item key={month} value={month}>
                      {month}
                    </Picker.Item>
                  ))}
                </Picker.Column>
                <Picker.Column name="day">
                  {endDays.map((day) => (
                    <Picker.Item key={day} value={day}>
                      {day}
                    </Picker.Item>
                  ))}
                </Picker.Column>
              </Picker>
            </div>
          </div>
        </Modal>
      </>
    );
  }

  // Desktop: Use Ant Design RangePicker
  return (
    <RangePicker
      value={value}
      onChange={(dates) => onChange?.(dates as [Dayjs | null, Dayjs | null] | null)}
      placeholder={placeholder}
      format={format}
      style={{ width: '100%', ...style }}
      size={size}
      allowClear={allowClear}
    />
  );
};

export default MobileDateRangePickerV2;
